import Channel, { ThreadChannel } from '@@types/Channel';
import { ID, LOG_ID, StringTMap, THREAD_CHANNEL_ID } from '@@types/common';
import Log, { AllThreadLog } from '@@types/Log';
import { Reaction } from '@@types/Log/Reaction';

import { ChannelType, MAIN_THREAD_CHANNEL_ID } from '~/constants';
import i18n from '~/i18n';
import GA from '~/services/GA/GA';
import Actions from '~/stores/Actions';
import { channelLogsStore } from '~/stores/ChannelLogsStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { mainDetailUiState, threadDetailUiState } from '~/stores/DetailUiState';
import { threadChannelLogsStore } from '~/stores/ThreadChannelLogsStore';
import { threadChannelStore } from '~/stores/ThreadChannelStore';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { wrapLoading } from '~/utils/fnUtil/fnUtil';
import { action, runInAction } from 'mobx';
import { last } from 'ramda';

import { api } from '../providers/Api2';

class ThreadChannelActions {
  constructor(private actions: Actions) {}

  @action
  public safeOpenThreadChannel = async (log: AllThreadLog) => {
    this.actions.coachMarkActions.completeMakeThreadCoachMark();
    const channelId = log.thread?.channelId;
    const totalCount = log.thread?.totalCount;

    if (!channelId || !totalCount) {
      runInAction(() => {
        threadDetailUiState.setCurrentChannelId(null);
        threadDetailUiState.setFetchedContentsChannelId(null);
        threadChannelLogsStore.clearDetailData();
        threadChannelLogsStore.setDummyParentLogId(log.id);
        (log.reactions ?? []).forEach((reactionId) => {
          threadChannelLogsStore.setReaction(reactionId, channelLogsStore.getReaction(reactionId));
        });
        this.openThreadLayer();
        this.actions.threadMainEditorActions.handleChangeContentsChannelId();
      });
      return;
    }
    await this.openThreadChannel(channelId);
  };

  public openThreadChannel = async (channelId: THREAD_CHANNEL_ID) => {
    this.actions.coachMarkActions.completeMakeThreadCoachMark();
    threadChannelLogsStore.setDummyParentLogId(null);
    const channel = await this.fetchThreadChannelInfo(channelId);
    threadDetailUiState.setCurrentChannelId(channelId);
    this.openThreadLayer();
    return channel;
  };

  private openThreadLayer = () => {
    runInAction(() => {
      uiState.setThreadOpen(true);
      uiState.setCurrentContext('thread');
    });
  };

  private fetchThreadChannelInfo = async (channelId: THREAD_CHANNEL_ID) => {
    const result = await api.channel.fetchThreadChannel(channelId);
    this.fetchThreadTaskList(channelId);
    const channel = result.content;
    runInAction(() => {
      threadChannelStore.updateChannel(channel);
      let parentChannel = result.references.channelMap[channel.parentChannelId];
      const parentChannelLog = result.references.channelLogMap[channel.parentChannelLogId];
      threadChannelLogsStore.mergeReactionMap(result.references.reactionMap ?? {});
      parentChannel && channelsStore.updateChannel(parentChannel);
      parentChannelLog &&
        threadChannelLogsStore.setParentLog(
          parentChannelLog,
          result.references.originalLogMap?.['originalLogId' in parentChannelLog ? parentChannelLog.originalLogId : '']
        );
    });
    return channel;
  };

  private fetchThreadTaskList = async (channelId: THREAD_CHANNEL_ID) => {
    const tasks = await api.channel.fetchThreadChannelTasks(channelId);
    threadChannelStore.setThreadChannelTasks(channelId, tasks);
  };

  public deleteTask = async (channelId: THREAD_CHANNEL_ID, taskId: ID) => {
    await this.actions.modalActions.openConfirm({
      description: i18n.t(
        'thread.deleteTaskAlert',
        '연결된 업무의 링크를 삭제 하시겠습니까?\n업무는 유지되며, 글타래에서 더 이상 노출되지 않습니다.'
      ),
    });
    await api.channel.deleteThreadChannelTask(channelId, taskId);
    const tasks = threadChannelStore.taskListMap[channelId];
    threadChannelStore.setThreadChannelTasks(
      channelId,
      tasks.filter((task) => task.id !== taskId)
    );
  };

  isCreating = false;
  public createThreadChannel = async () => {
    const parentChannelId = threadChannelLogsStore.threadPanelParentLog.channelId;
    const channel = channelsStore.getById(parentChannelId);
    threadChannelLogsStore.setIgnoreThreadChannelCreate(threadChannelLogsStore.dummyParentLogId);
    if (this.isCreating) {
      return Promise.reject();
    }
    this.isCreating = true;
    try {
      GA.writeThread({ action: '채널', label: '생성' });
      const threadChannel = await api.channel.createThreadChannel({
        parentChannelId,
        parentChannelLogId: threadChannelLogsStore.dummyParentLogId,
        capacity: channel.capacity,
        type: ChannelType.Thread,
        members: [userStore.id],
      });
      runInAction(() => {
        threadChannelStore.addChannel(threadChannel);
        threadDetailUiState.setCurrentChannelId(threadChannel.channelId);
        const parentLog = channelLogsStore.getById(threadChannelLogsStore.dummyParentLogId);
        parentLog && threadChannelLogsStore.setParentLog(parentLog);
        threadChannelLogsStore.setDummyParentLogId(null);
        threadDetailUiState.setFetchedContentsChannelId(threadChannel.channelId);
        threadChannelLogsStore.setIgnoreThreadChannelCreate(null);
      });

      return threadChannel;
    } finally {
      this.isCreating = false;
    }
  };

  public createThreadChannelFromEditorIfNeed = async () => {
    if (threadChannelLogsStore.dummyParentLogId) {
      uiState.setCreateThreadChannelBy('editor');
      const { channelId } = await this.createThreadChannel();
      await this.actions.slashCommandAppActions.fetchChannelApps(channelId);
    }
    return Promise.resolve();
  };

  @action
  public clearThreadPanel = ({
    close = true,
    dummyParentLogId = null,
  }: { close?: boolean; dummyParentLogId?: LOG_ID } = {}) => {
    threadDetailUiState.setCurrentChannelId(null);
    threadDetailUiState.setFetchedContentsChannelId('');
    threadChannelLogsStore.setDummyParentLogId(dummyParentLogId);
    threadChannelLogsStore.clearDetailData();
    if (close) {
      uiState.setThreadOpen(false);
      uiState.setCurrentContext('main');
      this.actions.channelMembersActions.updateChannelReadSeq(
        channelsStore.getById(mainDetailUiState.contentsChannelId)
      );
    }
  };

  public changeFilter = (filter: 'all' | 'unread') => {
    threadChannelLogsStore.setLogFilter(filter);
    threadChannelLogsStore.clearMainThreadChannelInfo();
    threadChannelStore.clearMainChannelList();
    threadChannelStore.clearUnusedData();
    uiState.setHasUpdatedThreadChannel(false);
    uiState.setHasUnreadThread(false);
    threadChannelStore.setMainThreadChannelAllLoaded(false);
    threadChannelStore.setMainThreadChannelPage(0);
    mainDetailUiState.setReplyReturnLog(null);
    channelLogsStore.setNewLog(null);
    this.fetchThreadMainChannel();
  };

  public fetchThreadMainChannel = async () => {
    const PAGE_SIZE = 20;
    await wrapLoading(
      async () => {
        const page = threadChannelStore.mainThreadChannelPage;
        let sentAt;
        if (page > 0) {
          const threadChannel = threadChannelStore.getById(last(threadChannelStore.mainThreadChannelIdList));
          sentAt = threadChannelLogsStore.getById(last(threadChannel.threadLogs)).sentAt;
        }
        let result = await api.channel.fetchThreadChannelList({
          page: threadChannelStore.mainThreadChannelPage,
          size: PAGE_SIZE,
          sentAt,
          filter: threadChannelLogsStore.logFilter,
        });
        threadChannelStore.setMainThreadChannelAllLoaded(result.contents.length < PAGE_SIZE);
        this.handleThreadMainChannelResult(result);
      },
      (loading) => {
        channelLogsStore.setIsFetchingLogs(loading);
        channelLogsStore.setIsLoadingLogs(loading);
      }
    );
  };

  @action
  private handleThreadMainChannelResult = (result: {
    contents: ThreadChannel[];
    references: {
      channelMap: StringTMap<Channel>;
      channelLogMap: StringTMap<Log>;
      originalLogMap: StringTMap<Log>;
      reactionMap: StringTMap<Reaction>;
    };
  }) => {
    const { contents, references = { channelLogMap: {}, originalLogMap: {}, channelMap: {}, reactionMap: {} } } =
      result;
    const { channelLogMap = {}, originalLogMap = {}, channelMap = {}, reactionMap = {} } = references;

    threadChannelStore.mergeMainChannelList(contents);
    channelLogsStore.mergeReferenceLogMap(originalLogMap);
    threadChannelLogsStore.mergeReferenceLogMap(originalLogMap);
    threadChannelLogsStore.mergeReactionMap(reactionMap);
    threadChannelLogsStore.mergeMainThreadChannelLogs(contents, channelLogMap);
    channelsStore.mergeChannels(Object.values(channelMap));
    mainDetailUiState.setFetchedContentsChannelId(MAIN_THREAD_CHANNEL_ID);
  };

  @action
  public fetchThreadChannelsStatus = async () => {
    const {
      content: { hasUnread },
    } = await api.channel.fetchThreadChannelStatus();
    uiState.setHasUnreadThread(hasUnread);
  };
}

export default ThreadChannelActions;
